import SelectContactModal from "@/components/features/contact/SelectContactModal/SelectContactModal";
import { Button, Typography } from "@/components/ui";
import type { Contact, DirectoryUser } from "@/lib/types";
import { formatContactsName, formatUsersName } from "@/lib/utils/format";
import { ChevronRight, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  isContactSigner,
  isUserSigner,
  type SignatureRequestSigner,
} from "../signatureRequestSigners";
import {
  getSignatureRequestVariableSignerSlot,
  getSignatureRequestVariableSignerSlots,
  type SignatureRequestVariableSignerSlot,
} from "../signatureRequestVariableUtils";
import SelectUserModal from "./SelectUserSlideover";

type UseSignerSlotAssignmentsOptions = {
  selectedDocumentVariables: string[];
  setSigners: React.Dispatch<React.SetStateAction<SignatureRequestSigner[]>>;
  signers: SignatureRequestSigner[];
};

export const useSignerSlotAssignments = ({
  selectedDocumentVariables,
  setSigners,
  signers,
}: UseSignerSlotAssignmentsOptions) => {
  const [activeSlot, setActiveSlot] =
    useState<SignatureRequestVariableSignerSlot | null>(null);
  const slots = useMemo(
    () => getSignatureRequestVariableSignerSlots(selectedDocumentVariables),
    [selectedDocumentVariables],
  );
  const contactSigners = useMemo(
    () => signers.filter(isContactSigner),
    [signers],
  );
  const userSigners = useMemo(() => signers.filter(isUserSigner), [signers]);
  const setContactAtSlot = useCallback(
    (index: number, contact: Contact) => {
      setSigners((previous) => {
        const signerIndex = previous.findIndex(
          (signer) => isContactSigner(signer) && signer.slotIndex === index,
        );
        const signer = { kind: "contact" as const, contact, slotIndex: index };

        if (signerIndex < 0) return [...previous, signer];

        return previous.map((current, currentIndex) =>
          currentIndex === signerIndex ? signer : current,
        );
      });
      setActiveSlot(null);
    },
    [setSigners],
  );
  const setUserAtSlot = useCallback(
    (index: number, user: DirectoryUser) => {
      setSigners((previous) => {
        const signerIndex = previous.findIndex(
          (signer) => isUserSigner(signer) && signer.slotIndex === index,
        );
        const signer = { kind: "user" as const, user, slotIndex: index };

        if (signerIndex < 0) return [...previous, signer];

        return previous.map((current, currentIndex) =>
          currentIndex === signerIndex ? signer : current,
        );
      });
      setActiveSlot(null);
    },
    [setSigners],
  );
  const activateVariable = useCallback((variable: string) => {
    const slot = getSignatureRequestVariableSignerSlot(variable);
    if (!slot) return false;
    setActiveSlot(slot);
    return true;
  }, []);
  const clearSignerAtSlot = useCallback(
    (slot: SignatureRequestVariableSignerSlot) => {
      setSigners((previous) =>
        previous.filter(
          (signer) =>
            signer.kind !== slot.kind || signer.slotIndex !== slot.index,
        ),
      );
    },
    [setSigners],
  );
  return {
    activateVariable,
    activeSlot,
    clearSignerAtSlot,
    contactSigners,
    setActiveSlot,
    setContactAtSlot,
    setUserAtSlot,
    slots,
    userSigners,
  };
};

type SignerSlotAssignmentsProps = ReturnType<
  typeof useSignerSlotAssignments
> & {
  matterId?: string;
};

export function SignerSlotAssignments({
  activateVariable,
  activeSlot,
  clearSignerAtSlot,
  contactSigners,
  matterId,
  setActiveSlot,
  setContactAtSlot,
  setUserAtSlot,
  slots,
  userSigners,
}: SignerSlotAssignmentsProps) {
  if (!slots.length) return null;

  const selectedContactIds = contactSigners
    .filter(
      (signer) =>
        activeSlot?.kind !== "contact" || signer.slotIndex !== activeSlot.index,
    )
    .map((signer) => signer.contact.id);
  const selectedUserIds = userSigners
    .filter(
      (signer) =>
        activeSlot?.kind !== "user" || signer.slotIndex !== activeSlot.index,
    )
    .map((signer) => signer.user.id);

  return (
    <div className="space-y-3">
      <div>
        <Typography className="text-base font-semibold">Signers</Typography>
      </div>
      {slots.map((slot) => {
        const signersForKind =
          slot.kind === "contact" ? contactSigners : userSigners;
        const signer = signersForKind.find(
          (candidate) => candidate.slotIndex === slot.index,
        );
        const name = signer
          ? isContactSigner(signer)
            ? formatContactsName(signer.contact)
            : formatUsersName(signer.user)
          : undefined;

        return (
          <div key={slot.key} className="space-y-2">
            <Typography className="text-sm font-medium">
              {slot.label}
            </Typography>
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                className={`relative w-full min-w-0 justify-start font-normal ${signer ? "pr-16" : "pr-10"}`}
                onClick={() => {
                  activateVariable(`${slot.key}_`);
                }}
              >
                <span className={name ? "truncate" : "text-muted-foreground"}>
                  {name ??
                    `Select a ${slot.kind === "contact" ? "contact" : "firm user"}`}
                </span>
                <ChevronRight className="text-muted-foreground absolute right-3 h-4 w-4" />
              </Button>
              {signer ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground absolute top-1/2 right-8 z-10 h-7 w-7 -translate-y-1/2"
                  aria-label={`Clear ${slot.label}`}
                  title={`Clear ${slot.label}`}
                  onClick={() => {
                    clearSignerAtSlot(slot);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>
        );
      })}

      <SelectContactModal
        open={activeSlot?.kind === "contact"}
        setOpen={(open) => {
          if (!open) setActiveSlot(null);
        }}
        onSelectContact={(contact) => {
          if (activeSlot?.kind === "contact") {
            setContactAtSlot(activeSlot.index, contact);
          }
        }}
        excludeContactIds={selectedContactIds}
        matterId={matterId}
        label=""
        title={activeSlot?.label ?? "Select contact signer"}
        description=""
      />
      <SelectUserModal
        open={activeSlot?.kind === "user"}
        multi={false}
        setOpen={(open) => {
          if (!open) setActiveSlot(null);
        }}
        onSelectUser={(user) => {
          if (activeSlot?.kind === "user") {
            setUserAtSlot(activeSlot.index, user);
          }
        }}
        excludeUserIds={selectedUserIds}
        title={activeSlot?.label ?? "Select firm signer"}
        description=""
      />
    </div>
  );
}
